import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Trash2 } from 'lucide-react';
import {
  dueInCurrency,
  formatMoney,
  fromBaseCents,
  methodEnabled,
  PAYMENT_METHODS,
  TENDER_METHODS,
  toBaseCents,
  type CurrencyCode,
  type PaymentMethod,
  type PaymentSettings,
} from '@tengesa/shared';
import { api, ApiError } from '../../lib/api';
import type { SaleDetail } from '../../lib/types';
import { uuid } from '../../lib/uuid';
import { useSession } from '../../app/session';
import { Button, Dialog, Field, TextInput } from '../../ui';
import { MoneyInput } from '../../ui/inputs';
import type { Cart } from './cart';
import type { PickedCustomer } from '../customers/CustomerPicker';

export interface TillCurrencies {
  baseCurrency: CurrencyCode;
  currencies: { currency: CurrencyCode; rate: string | null; rateMicros: number | null; effectiveFrom: string | null }[];
  paymentSettings: PaymentSettings;
}

export function useTillCurrencies() {
  return useQuery({ queryKey: ['till-currencies'], queryFn: () => api.get<TillCurrencies>('/api/till/currencies'), staleTime: 60_000 });
}

interface Tender {
  method: PaymentMethod;
  currency: CurrencyCode;
  amountCents: number;
  reference: string | null;
}

/** Notes and coins worth offering as one-tap cash, per currency (in cents). */
const DENOMINATIONS: Partial<Record<CurrencyCode, number[]>> = {
  USD: [100, 200, 500, 1000, 2000, 5000, 10_000],
  ZWG: [1000, 2000, 5000, 10_000, 20_000],
  ZAR: [1000, 2000, 5000, 10_000, 20_000],
};

export function PaymentDialog({
  cart,
  customer,
  totalCents,
  branchId,
  onClose,
  onDone,
}: {
  cart: Cart;
  customer: PickedCustomer | null;
  totalCents: number;
  branchId: string;
  onClose: () => void;
  onDone: (sale: SaleDetail) => void;
}) {
  const { me, can } = useSession();
  const base = me.business.baseCurrency as CurrencyCode;
  const till = useTillCurrencies();
  // One id per payment attempt on this cart: a retry after a dropped connection can't sell twice.
  const clientRef = useMemo(() => uuid(), []);
  const settings = till.data?.paymentSettings;
  const methods = TENDER_METHODS.filter((m) => methodEnabled(settings, m)).filter((m) => m !== 'account' || (customer && customer.creditLimitCents > 0 && can('credit.grant')));
  const currencies = (till.data?.currencies ?? [{ currency: base, rate: '1', rateMicros: 1_000_000, effectiveFrom: null }]).filter((c) => c.rateMicros != null);
  const rateOf = (c: CurrencyCode) => (c === base ? null : (currencies.find((x) => x.currency === c)?.rateMicros ?? null));
  const toBase = (c: CurrencyCode, cents: number) => {
    const r = rateOf(c);
    return r == null ? cents : toBaseCents(cents, r);
  };
  const dueIn = (c: CurrencyCode, baseCents: number) => {
    const r = rateOf(c);
    return r == null ? baseCents : dueInCurrency(baseCents, r);
  };
  const fmt = (c: number, cur: CurrencyCode = base) => formatMoney(c, cur);

  const [tenders, setTenders] = useState<Tender[]>([]);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [currency, setCurrency] = useState<CurrencyCode>(base);
  const [changeCurrency, setChangeCurrency] = useState<CurrencyCode>(base);
  const paidBase = tenders.reduce((a, t) => a + toBase(t.currency, t.amountCents), 0);
  const remainingBase = Math.max(totalCents - paidBase, 0);
  const [amount, setAmount] = useState<number | null>(totalCents);
  const [reference, setReference] = useState('');
  const needsRef = PAYMENT_METHODS[method].needsReference;
  const cashBase = tenders.filter((t) => t.method === 'cash').reduce((a, t) => a + toBase(t.currency, t.amountCents), 0);
  const nonCashBase = paidBase - cashBase;
  const changeBase = Math.max(paidBase - totalCents, 0);
  // Mirrors the server: paid and changed in the same foreign currency → change worked out in it.
  const sameForeign = changeCurrency !== base && tenders.length > 0 && tenders.every((t) => t.currency === changeCurrency);
  const changeShown = changeBase === 0 ? 0 : sameForeign ? Math.max(0, tenders.reduce((a, t) => a + t.amountCents, 0) - dueIn(changeCurrency, totalCents)) : rateOf(changeCurrency) == null ? changeBase : fromBaseCents(changeBase, rateOf(changeCurrency)!);
  const nonCashTolerance = tenders.filter((t) => t.method !== 'cash' && t.currency !== base).length * 2;
  const ready = paidBase >= totalCents && nonCashBase <= totalCents + nonCashTolerance && !till.isLoading;
  const account = method !== 'cash' ? settings?.[method as keyof PaymentSettings]?.account : null;

  const pick = (m: PaymentMethod, c: CurrencyCode = currency) => {
    const cur = m === 'account' ? base : c;
    setMethod(m);
    setCurrency(cur);
    setAmount(dueIn(cur, remainingBase) || null);
  };

  const addTender = (amt: number | null = amount) => {
    if (!amt || amt <= 0) return;
    if (needsRef && !reference.trim()) return;
    const next = [...tenders, { method, currency, amountCents: amt, reference: reference.trim() || null }];
    setTenders(next);
    const left = Math.max(totalCents - next.reduce((a, t) => a + toBase(t.currency, t.amountCents), 0), 0);
    setAmount(dueIn(currency, left) || null);
    setReference('');
    setMethod('cash');
    return next;
  };

  const sale = useMutation({
    mutationFn: (payments: Tender[]) =>
      api.post<SaleDetail>('/api/sales', {
        clientRef,
        branchId,
        customerId: customer?.id ?? null,
        lines: cart.lines.map((l) => ({
          productId: l.product.id,
          qtyMilli: l.qtyMilli,
          unitPriceCents: l.unitPriceCents,
          manualDiscount: l.manualDiscount,
        })),
        saleDiscount: cart.saleDiscount,
        payments,
        changeCurrency,
      }),
    onSuccess: onDone,
  });

  const complete = (payments = tenders) => sale.mutate(payments);
  /** One-tap: tender this amount in cash and finish if it covers the sale. */
  const quickCash = (amt: number) => {
    const next = [...tenders, { method: 'cash' as const, currency, amountCents: amt, reference: null }];
    const total = next.reduce((a, t) => a + toBase(t.currency, t.amountCents), 0);
    setTenders(next);
    if (total >= totalCents) complete(next);
    else setAmount(dueIn(currency, totalCents - total));
  };

  const networkFailed = sale.error instanceof ApiError && sale.error.status === 0;
  const remainingHere = dueIn(currency, remainingBase);

  return (
    <Dialog
      open
      onClose={sale.isPending ? () => {} : onClose}
      title={`Take payment — ${fmt(totalCents)}`}
      wide
      footer={
        <>
          <Button onClick={onClose} disabled={sale.isPending}>
            Back to sale
          </Button>
          <Button variant="primary" className="min-w-40" disabled={!ready} busy={sale.isPending} onClick={() => complete()}>
            Complete sale
          </Button>
        </>
      }
    >
      {sale.error && (
        <div className="mb-4 rounded-[8px] bg-danger-soft px-3 py-2 text-sm text-danger" role="alert">
          {sale.error.message}
          {networkFailed && ' The sale may or may not have gone through. Pressing Complete sale again is safe: it will not charge twice.'}
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-[1fr_270px]">
        <div>
          {currencies.length > 1 && (
            <div className="mb-4">
              <p className="mb-2 text-sm font-medium">Currency</p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Currency">
                {currencies.map((c) => (
                  <button
                    key={c.currency}
                    role="radio"
                    aria-checked={currency === c.currency}
                    disabled={method === 'account' && c.currency !== base}
                    onClick={() => pick(method, c.currency)}
                    className={clsx(
                      'rounded-[8px] border px-3 py-2 text-left text-sm disabled:opacity-40',
                      currency === c.currency ? 'border-ledger-600 bg-ledger-50 text-ledger-800' : 'border-line-strong hover:bg-paper',
                    )}
                  >
                    <span className="block font-medium">{c.currency}</span>
                    <span className="block text-xs text-muted num">{fmt(dueIn(c.currency, remainingBase), c.currency)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="mb-2 text-sm font-medium">Payment method</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {methods.map((m) => (
              <button
                key={m}
                onClick={() => pick(m)}
                className={clsx(
                  'rounded-[8px] border px-3 py-2.5 text-left text-sm font-medium',
                  method === m ? 'border-ledger-600 bg-ledger-50 text-ledger-800' : 'border-line-strong hover:bg-paper',
                )}
              >
                {PAYMENT_METHODS[m].label.replace(' (Zimswitch / Visa / Mastercard)', '')}
              </button>
            ))}
          </div>
          {account && <p className="mt-2 text-sm text-muted">Customer pays to: <span className="font-medium text-ink">{account}</span></p>}

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label={`Amount (${currency})`} htmlFor="pay-amount" hint={currency !== base ? `Rate ${currencies.find((c) => c.currency === currency)?.rate} per ${base} 1` : undefined}>
              <MoneyInput id="pay-amount" currency={currency} value={amount} onChange={setAmount} autoFocus onEnter={() => (needsRef ? undefined : addTender())} />
            </Field>
            {needsRef && (
              <Field label="Transaction reference" htmlFor="pay-ref" hint="From the customer's confirmation message">
                <TextInput id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTender()} />
              </Field>
            )}
          </div>
          <Button className="mt-3" onClick={() => addTender()} disabled={!amount || (needsRef && !reference.trim())}>
            Add {PAYMENT_METHODS[method].label.split(' (')[0]} payment
          </Button>

          {method === 'cash' && remainingBase > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-sm font-medium">Quick cash</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => quickCash(remainingHere)}>
                  Exact {fmt(remainingHere, currency)}
                </Button>
                {(DENOMINATIONS[currency] ?? [])
                  .filter((d) => d > remainingHere)
                  .slice(0, 4)
                  .map((d) => (
                    <Button size="sm" key={d} onClick={() => quickCash(d)}>
                      {fmt(d, currency)}
                    </Button>
                  ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-[10px] bg-paper p-4">
          <dl className="space-y-1.5">
            <div className="flex justify-between">
              <dt className="text-muted">Due</dt>
              <dd className="font-medium num">{fmt(totalCents)}</dd>
            </div>
            {tenders.map((t, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm">
                <dt className="min-w-0 truncate">
                  {PAYMENT_METHODS[t.method].label.split(' (')[0]}
                  {t.reference && <span className="text-muted"> {t.reference}</span>}
                </dt>
                <dd className="flex items-center gap-1 num">
                  {fmt(t.amountCents, t.currency)}
                  <button aria-label="Remove payment" className="rounded p-1 text-muted hover:text-danger" onClick={() => setTenders(tenders.filter((_, j) => j !== i))}>
                    <Trash2 className="size-3.5" />
                  </button>
                </dd>
              </div>
            ))}
            <div className="flex justify-between border-t border-line pt-2">
              <dt className="text-muted">Still to pay</dt>
              <dd className="font-semibold num">{fmt(remainingBase)}</dd>
            </div>
          </dl>
          <div className="mt-4 rounded-[8px] bg-surface p-3 text-center">
            <p className="text-sm text-muted">Change</p>
            <p className="text-3xl font-semibold num">{fmt(changeShown, changeCurrency)}</p>
            {currencies.length > 1 && (
              <label className="mt-2 flex items-center justify-center gap-2 text-sm text-muted">
                Give change in
                <select
                  className="h-8 rounded-[6px] border border-line-strong bg-surface px-2 text-ink"
                  value={changeCurrency}
                  onChange={(e) => setChangeCurrency(e.target.value as CurrencyCode)}
                >
                  {currencies.map((c) => <option key={c.currency} value={c.currency}>{c.currency}</option>)}
                </select>
              </label>
            )}
          </div>
          {nonCashBase > totalCents + nonCashTolerance && <p className="mt-2 text-sm text-danger">Card, mobile, bank and account payments can't be more than the amount due.</p>}
          {customer && method === 'account' && (
            <p className="mt-2 text-sm text-muted">
              {customer.name} can take up to {fmt(customer.availableCreditCents)} more on account.
            </p>
          )}
          {till.data?.currencies.some((c) => c.rateMicros == null) && (
            <p className="mt-2 text-xs text-muted">
              {till.data.currencies.filter((c) => c.rateMicros == null).map((c) => c.currency).join(', ')} can't be taken until a manager sets the rate.
            </p>
          )}
        </div>
      </div>
    </Dialog>
  );
}
